import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX, FiAlertCircle, FiCheckCircle, FiInfo, FiAlertTriangle } from 'react-icons/fi';

const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-20px);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ToastItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(26, 12, 46, 0.9);
  color: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.25);
  max-width: 400px;
  width: 100%;
  backdrop-filter: blur(10px);
  animation: ${props => props.closing ? slideOut : slideIn} 0.3s ease;
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'error':
        return '#ff3b5c';
      case 'success':
        return '#4caf50';
      case 'warning':
        return '#ffb100';
      case 'info':
      default:
        return '#ff40c0';
    }
  }};
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  color: ${props => {
    switch (props.type) {
      case 'error':
        return '#ff3b5c';
      case 'success':
        return '#4caf50';
      case 'warning':
        return '#ffb100';
      case 'info':
      default:
        return '#ff40c0';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageContent = styled.div`
  flex: 1;
  font-size: 0.95rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  font-size: 1.1rem;
  transition: all 0.2s;
  
  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Toast = ({ type = 'info', message, onClose, autoClose = 5000 }) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'error':
        return <FiAlertCircle />;
      case 'success':
        return <FiCheckCircle />;
      case 'warning':
        return <FiAlertTriangle />;
      case 'info':
      default:
        return <FiInfo />;
    }
  };

  return (
    <ToastItem type={type}>
      <IconWrapper type={type}>
        {getIcon()}
      </IconWrapper>
      <MessageContent>{message}</MessageContent>
      <CloseButton onClick={onClose}>
        <FiX />
      </CloseButton>
    </ToastItem>
  );
};

const ToastManager = ({ toasts, removeToast }) => {
  return (
    <ToastContainer>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          onClose={() => removeToast(toast.id)}
          autoClose={toast.autoClose}
        />
      ))}
    </ToastContainer>
  );
};

export default ToastManager; 